import type { LocationQueryRaw, RouteLocationRaw } from 'vue-router'
import { useProjectsStore } from '@/stores/projects'

/** The two assessable project tracks. `regulatory_monitoring` projects have no
 *  assessment surface at all and never reach this resolver. */
export type ProjectTrack = 'qms' | 'td'

/**
 * Where opening a project lands (spec §5, amended 2026-09-08).
 *
 * ONE resolver, because there are several callers that used to hard-code
 * `qms-assessments` / `products-assessments`: the two project root routes, the
 * feature-flag-off fallbacks and the dashboard MAC tile. Every one of them was
 * a place the landing page could be got wrong independently.
 *
 * Legacy assessment data does not change the answer. The original rule ("a
 * project that already has legacy assessment data stays exactly as it is
 * today", principles §1) held while the referentials page was the new surface
 * and the legacy one was the one people knew. That is now inverted:
 * referentials is the surface worth opening on, and the legacy assessments
 * page is history a user goes looking for. So a legacy project opens on
 * referentials AND keeps its «Assessments · History» nav entry — see
 * `showsAssessmentsNav`.
 */
export function projectHome(pid: string, track: ProjectTrack): RouteLocationRaw {
  return referentialsRouteFor(pid, { track })
}

/**
 * A project's referentials screen (its Pilotage), QMS or product (MYAC-1418).
 *
 * ONE place picks the route by project type, because the callers that knew only
 * a pid hard-coded `qms-referentials` and leaned on the router's wrong-module
 * redirect to land a product right. `track` when the caller already knows it;
 * otherwise it is read off the loaded project list, and an unknown project
 * reads as QMS, where the router's redirect still corrects it.
 */
export function referentialsRouteFor(
  pid: string,
  opts: { track?: ProjectTrack; query?: LocationQueryRaw } = {},
): RouteLocationRaw {
  const track = opts.track ?? trackOfProject(pid)
  const name = track === 'td' ? 'products-referentials' : 'qms-referentials'
  return opts.query ? { name, params: { pid }, query: opts.query } : { name, params: { pid } }
}

function trackOfProject(pid: string): ProjectTrack {
  const type = useProjectsStore().projects.find((p) => p.id === pid)?.type
  return type === 'td' ? 'td' : 'qms'
}

/**
 * Does the nav show the «Assessments» entry? (spec §5, amended 2026-09-08)
 *
 * It does NOT mirror `projectHome`: a legacy project lands on referentials and
 * still shows this entry, because the legacy data is the reason to keep the
 * door open, not a reason to open on it.
 *
 * `legacy` is the tri-state read of the memoized probe: `undefined` means "not
 * answered yet" and counts as legacy, because the failure that matters is
 * hiding a surface a project owns — never the reverse.
 */
export function showsAssessmentsNav(legacy: boolean | undefined): boolean {
  return legacy !== false
}
